import React, { useEffect, useState } from 'react';
import { ReactComponent as ArrowLeft } from 'icons/arrow-left.svg';
import { Button, InputField, TableWrapper } from 'components/Global';
import { useDispatch, useSelector } from 'react-redux';
import { useAppContext } from 'components/Context/AppContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { checkEmptyObject, checkPermission } from 'utils/functionality';
import { getAllRentalLocation } from 'features/rental-location/actions';
import { ReactComponent as EventMasterIcon } from 'icons/event-master-filled-icon.svg';
import TextAreaInput from 'components/Global/TextArea';
import SelectFieldDate from 'components/Global/SelectFieldDate';
import { format, isEqual, parseISO } from 'date-fns';
import { ReactComponent as Mappin } from 'icons/mappin-icon.svg';
import SelectFieldDropdown from 'components/Global/SelectFieldDropdown';
import { addNewEvent, getEventDetail, updateEventById } from 'features/holiday-charge/actions';
import { trackEvent } from 'utils/analytics';
import { resetSelectedEvent } from 'features/holiday-charge/eventSlice';

const EventMasterDetail = () => {
  const { offCanvasMenu, currentMenu } = useSelector((state) => state.menu);
  const { data: rentalLocationData } = useSelector((state) => state.rentalLocation);
  const selectedEventData = useSelector((state) => state.events.selected);

  const { showToast, setShowConfirmation } = useAppContext();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [eventData, setEventData] = useState({
    id: 0,
    name: '',
    description: '',
    start_date: '',
    end_date: '',
  });
  const [selectedDate, setSelectedDate] = useState({
    from: null,
    to: null,
  });
  const [mappedLocationList, setMappedLocationList] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState([]);

  const [searchParams] = useSearchParams();

  const id = searchParams.get('id');

  const isAddState = location.pathname.includes('add');

  useEffect(() => {
    if (id) {
      dispatch(getEventDetail(id));
      setSelectedLocation(0);
    }
  }, [dispatch, id]);

  useEffect(() => {
    dispatch(getAllRentalLocation());
  }, [dispatch]);

  useEffect(() => {
    if (checkEmptyObject(selectedEventData)) return;
    setEventData({
      id: selectedEventData.id,
      name: selectedEventData.name,
      description: selectedEventData.description,
      start_date: selectedEventData.date,
      end_date: selectedEventData.end_date,
    });
    setSelectedDate({
      from: parseISO(selectedEventData.date),
      to: selectedEventData.date === selectedEventData.end_date ? undefined : parseISO(selectedEventData.end_date),
    });
    setSelectedLocation(selectedEventData.location_id);
  }, [selectedEventData]);

  useEffect(() => {
    if (!rentalLocationData?.length) return;
    const newSelectedLocationArr = rentalLocationData.map((item) => ({ ...item, value: item.name.toLowerCase() }));
    setMappedLocationList(newSelectedLocationArr);
  }, [rentalLocationData]);

  const backHandler = () => {
    navigate(-1);
  };

  const updateHandler = async () => {
    if (eventData.name.trim() === '' || eventData.description.trim() === '') {
      showToast({ type: 'warning', message: 'Harap Isi Nama dan Deskripsi Event' });
      return;
    }
    if (
      (typeof selectedLocation === 'object' && !selectedLocation.length) ||
      (typeof selectedLocation === 'number' && !selectedLocation)
    ) {
      showToast({ type: 'warning', message: 'Harap Memilih Lokasi Event' });
      return;
    }
    if (eventData.start_date.trim() === '' || eventData.end_date.trim() === '') {
      showToast({ type: 'warning', message: 'Harap Isi Periode Event' });
      return;
    }

    const payload = {
      name: eventData.name,
      desc: eventData.description,
      date: eventData.start_date,
      end_date: eventData.end_date,
      location_ids: isAddState ? selectedLocation.map((item) => item.id) : selectedLocation,
    };

    setShowConfirmation({
      message: isAddState ? 'Apakah Anda yakin ingin menyimpan data ini?' : 'Apakah Anda ingin mengubah data ini?',
      show: true,
      onClick: async () => {
        try {
          if (isAddState) {
            trackEvent('event_created', { source: 'event_master_detail' });
            await dispatch(addNewEvent(payload)).unwrap();
            showToast({ type: 'success', message: 'Berhasil Menambahkan Data Event' });
          } else {
            trackEvent('event_updated', { event_id: id, source: 'event_master_detail' });
            await dispatch(updateEventById({ id: id, payload: payload })).unwrap();
            showToast({ type: 'success', message: 'Berhasil Mengubah Data Event' });
          }
          dispatch(resetSelectedEvent());
        } catch (error) {
          showToast({ type: 'error', message: `Terjadi Kesalahan: ${error.slug}` });
        } finally {
          navigate('/event-master');
        }
      },
    });
  };

  const handleLocation = (locationId, status) => {
    if (isAddState) {
      const existedType = selectedLocation?.find(
        (item) => item.id === rentalLocationData.find((el) => el.id == locationId)?.id,
      );
      // if exist will return nothing
      if (existedType && status) return;
      const locationValue = mappedLocationList?.find((el) => el.id == locationId);

      if (status) {
        setSelectedLocation(() => [
          ...selectedLocation,
          { id: locationValue.id, name: locationValue.name, value: locationValue.name.toLowerCase() },
        ]);
      } else {
        setSelectedLocation((prev) => prev.filter((item) => item.id !== locationValue.id));
      }
    } else {
      setSelectedLocation(+locationId.target.id);
    }
  };

  return (
    <div className="event-master">
      <div className="event-master-detail">
        <button className="event-master-detail__back-btn" onClick={backHandler}>
          <ArrowLeft />
          <span>Kembali</span>
        </button>

        <TableWrapper title="Event Master Data" icon={<EventMasterIcon fill="#FF9900" width="25px" height="25px" />}>
          <h3 className="event-master-detail__title">Set Event Master Data</h3>
          <div className="event-master-detail__content">
            <InputField
              label="Nama Hari Libur"
              htmlFor="holiday-name"
              className="holiday-name"
              autoComplete="off"
              value={eventData?.name || ''}
              onChange={(e) => setEventData((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Nama Hari Libur"
            />
          </div>
          <div className="event-master-detail__content">
            <TextAreaInput
              label="Deskripsi"
              htmlFor="event-description"
              className="event-description"
              autoComplete="off"
              value={eventData?.description || ''}
              onChange={(e) => setEventData((prev) => ({ ...prev, description: e.target.value }))}
              rows={4}
              placeholder="Deskripsi"
            />
          </div>
          <div className="event-master-detail__content location-date">
            <SelectFieldDropdown
              label="Lokasi"
              placeholder="Pilih Lokasi"
              data={mappedLocationList}
              value={selectedLocation}
              onChange={handleLocation}
              checkBox={isAddState}
              htmlFor="location"
              icon={<Mappin className="select-field-shuttle__input-field__arrow" />}
            />
            <SelectFieldDate
              label="Periode Holiday Charge"
              htmlFor="tanggal-periode"
              name="tanggal-periode"
              placeholder="Pilih Tanggal Periode"
              selectedDay={selectedDate}
              value={
                selectedDate.from
                  ? selectedDate.from && !selectedDate.to
                    ? format(selectedDate.from, 'dd MMMM yyyy')
                    : format(selectedDate.from, 'dd MMMM - ').concat(format(selectedDate.to, 'dd MMMM yyyy'))
                  : ''
              }
              defaultMonth={selectedDate.from}
              fromDate={new Date()}
              handleDaySelect={(date) => {
                if (date) {
                  if (isEqual(date.from, date.to)) {
                    setSelectedDate({
                      from: null,
                      to: null,
                    });
                    setEventData({
                      ...eventData,
                      start_date: '',
                      end_date: '',
                    });
                    return;
                  }
                  setSelectedDate(date);
                  setEventData({
                    ...eventData,
                    start_date: format(date.from, 'yyyy-MM-dd'),
                    end_date: date.to ? format(date.to, 'yyyy-MM-dd') : format(date.from, 'yyyy-MM-dd'),
                  });
                } else {
                  setSelectedDate({
                    from: null,
                    to: null,
                  });
                  setEventData({
                    ...eventData,
                    start_date: '',
                    end_date: '',
                  });
                }
              }}
              dateContainerXOffset="left"
              mode="range"
            />
          </div>

          <div className="event-master-detail__button">
            <Button className="button" size="sm" width="192px" onClick={backHandler} variant="outline">
              Kembali
            </Button>
            {(isAddState || checkPermission(offCanvasMenu, currentMenu, 'update')) && (
              <Button className="button" size="sm" width="192px" onClick={updateHandler}>
                Simpan
              </Button>
            )}
          </div>
        </TableWrapper>
      </div>
    </div>
  );
};

export default EventMasterDetail;
